import { PlatformTypes, Post } from "./types";
import { PostMediaFile } from "../media-library/types";
import {
  parseGoogleBusinessData,
  parseLinkedInData,
  parsePinterestData,
  parseThreadsData,
  parseTikTokData,
  parseYoutubeData,
} from "./format-post";
import { VIDEO_REEL_CONSTANTS } from "../../constants/VideoReelConstants";
import { isReelPost } from "./is-reel-post";
import { pickStoryFields } from "./pick-story-fields";

type PostPlatformValue = NonNullable<Post["platforms"]>[number];

function normalizePlatformTypeForApi(
  platformType: string | undefined
): PlatformTypes {
  switch (platformType) {
    case "IG":
      return PlatformTypes.instagram;
    case "FB":
      return PlatformTypes.facebook;
    case "TW":
      return PlatformTypes.twitter;
    case "LI":
      return PlatformTypes.linkedin;
    case "GMB":
      return PlatformTypes.googlemybusiness;
    case "YT":
      return PlatformTypes.youtube;
    case "PI":
      return PlatformTypes.pinterest;
    case "TT":
      return PlatformTypes.tiktok;
    case "TH":
      return PlatformTypes.threads;
    case "SC":
      return PlatformTypes.snapchat;
    case "OT":
      return PlatformTypes.other;
    case "LINKED":
      return PlatformTypes.cluster;
    default:
      return PlatformTypes.instagram;
  }
}

export type PostApiInput = Partial<Post> & {
  [key: string]: unknown;
  caption: string;
  mediaFiles: PostMediaFile[];
  location: unknown;
};

function getPlatformCode(platform: PostPlatformValue): PlatformTypes {
  return normalizePlatformTypeForApi(
    typeof platform === "string" ? platform : platform.platformType
  );
}

function canSelectVideoCoverFrame(platform: string): boolean {
  return [
    PlatformTypes.instagram,
    PlatformTypes.facebook,
    PlatformTypes.linkedin,
    PlatformTypes.tiktok,
    PlatformTypes.cluster,
  ].includes(normalizePlatformTypeForApi(platform));
}

function normalizeAssetIdForApi(assetId?: string): string | undefined {
  return /^[a-f\d]{24}$/i.test(assetId ?? "") ? assetId : undefined;
}

function normalizeMediaFileForApi(
  mediaFile: Post["mediaFiles"][number]
): PostMediaFile {
  const rest = { ...mediaFile } as Post["mediaFiles"][number] & {
    _id?: string;
  };
  delete rest._id;

  return {
    ...rest,
    originalUrl: mediaFile.originalUrl || mediaFile.origUrl || mediaFile.url,
    asset_id: normalizeAssetIdForApi((mediaFile as PostMediaFile).asset_id),
  } as PostMediaFile;
}

export function getPostDataFromTypedPost(post: Post): PostApiInput {
  const postRootMediaData = parseMediaFileToPostRootData(post);
  const primaryMediaIsVideo = post.mediaFiles?.[0]?.isVideo === true;

  // Derive platform types from accounts as fallback — the form post object
  // has post.accounts but NOT post.platforms (which is only computed in the
  // old parseFormDataToPost path). Without this, cluster posts from the form
  // would have hasGMB/hasPI/etc. all false, dropping all platform fields.
  const accountPlatforms =
    post.accounts?.map((a) => a.platformType || "IG") || [];

  // Determine platform membership — check platformType enum, platforms string
  // array, AND accounts array to handle all code paths
  const uniqueAccountPlatforms = [...new Set(accountPlatforms)];
  const platformCodes = (post.platforms ?? []).map(getPlatformCode);
  const isCluster =
    post.platformType === PlatformTypes.cluster ||
    uniqueAccountPlatforms.length > 1;
  const hasIG =
    post.platformType === PlatformTypes.instagram ||
    platformCodes.includes(PlatformTypes.instagram) ||
    accountPlatforms.includes("IG");
  const hasFB =
    post.platformType === PlatformTypes.facebook ||
    platformCodes.includes(PlatformTypes.facebook) ||
    accountPlatforms.includes("FB");
  const hasGMB =
    post.platformType === PlatformTypes.googlemybusiness ||
    platformCodes.includes(PlatformTypes.googlemybusiness) ||
    accountPlatforms.includes("GMB");
  const hasLI =
    post.platformType === PlatformTypes.linkedin ||
    platformCodes.includes(PlatformTypes.linkedin) ||
    accountPlatforms.includes("LI");
  const hasPI =
    post.platformType === PlatformTypes.pinterest ||
    platformCodes.includes(PlatformTypes.pinterest) ||
    accountPlatforms.includes("PI");
  const hasYT =
    post.platformType === PlatformTypes.youtube ||
    platformCodes.includes(PlatformTypes.youtube) ||
    accountPlatforms.includes("YT");
  const hasTT =
    post.platformType === PlatformTypes.tiktok ||
    platformCodes.includes(PlatformTypes.tiktok) ||
    accountPlatforms.includes("TT");
  const hasTH =
    post.platformType === PlatformTypes.threads ||
    platformCodes.includes(PlatformTypes.threads) ||
    accountPlatforms.includes("TH");

  // Video/reel logic. Facebook reels use the dedicated `fb-reel` job type;
  // other platforms (IG, TT, LI, etc.) ride the regular video pipeline with
  // a `videoPostType: "reel"` hint, so we must never emit a raw
  // `type: "reel"` payload — the backend has no handler for it.
  //
  // Guard on `post.isVideo` so a stale `videoPostType: "reel"` left on an
  // image/document post (e.g. the reel toggle was set then media was
  // swapped without clearing the flag) doesn't get serialized as a video.
  // Matches the equivalent check in
  // `apps/schedugram-client/react-src/sagas/postsSagas.ts:1509`. SKE-3646
  // A story is never a reel, even with a stale reel flag left on it: a 9:16
  // Facebook story video would otherwise leave here as an fb-reel.
  const isVideoReel = isReelPost({
    isStory: post.isStory,
    isVideo: post.isVideo || primaryMediaIsVideo || post.type === "fb-reel",
    videoPostType:
      post.type === "reel" || post.type === "fb-reel"
        ? VIDEO_REEL_CONSTANTS.POST_TYPES.REEL
        : post.videoPostType,
  });
  const videoReelData = isVideoReel
    ? hasFB
      ? {
          isFbReel: true,
          type: "fb-reel",
          videoPostType: VIDEO_REEL_CONSTANTS.POST_TYPES.REEL,
          isVideo: false,
        }
      : {
          type: "video",
          videoPostType: VIDEO_REEL_CONSTANTS.POST_TYPES.REEL,
          isVideo: true,
        }
    : {};

  const storyData =
    hasIG || (hasFB && post.isStory)
      ? { isStory: post.isStory, ...pickStoryFields(post, { forApi: true }) }
      : {};

  // Parse platform-specific data via shared helpers
  const tikTokData = hasTT ? parseTikTokData(post as never) : {};
  const threadsData = hasTH ? parseThreadsData(post as never) : {};

  const data = {
    _id: post._id,
    accountIds: post.accountIds,
    postType: post.state,
    channel: post.channel,
    caption: post.caption || "",
    labelIds: post.labelIds || [],
    postClusterId: post.postClusterId,
    ideaId: post.ideaId,
    firstcomment: post.firstcomment || "",
    ...postRootMediaData,
    mediaFiles: post.mediaFiles?.map(normalizeMediaFileForApi),
    // For non-FB reels we serialize as plain video; videoReelData below
    // overrides this when isVideoReel is set. Never let `type: "reel"`
    // through unchanged — the server has no handler for it. SKE-3646
    type: isVideoReel ? (hasFB ? "fb-reel" : "video") : post.type,
    when: post.when,
    isNow:
      post.state === "draft" || post.state === "queue" ? false : post.isNow,
    redirectUrl: post.redirectUrl || null,
    postStatusKey: post.postStatusKey,
    videoPostType: isVideoReel
      ? VIDEO_REEL_CONSTANTS.POST_TYPES.REEL
      : VIDEO_REEL_CONSTANTS.POST_TYPES.VIDEO,
    ...videoReelData,

    // Platform-specific fields — each fires once based on hasXX flags.
    // Uses shared helpers from format-post.ts to ensure consistent defaults
    // and topic-type-conditional logic (e.g. GMB only sends relevant fields).

    ...(hasIG
      ? {
          /*
           * SKE-4539. Meta added story user tags on 9 July 2025 for BOTH image
           * and video stories, and the backend already sends them. Emptying
           * them here was what made a tagged story publish untagged.
           *
           * Product tags stay stripped: Meta documents those for feed images
           * and carousels, not stories.
           */
          tags: post.tags,
          firstcomment: post.firstcomment || "",
          productTags: post.isStory ? [] : post.productTags,
          location: post.location,
          collaboratingIgUsers: post.collaboratingIgUsers || [],
          ...(post.instagram ? { instagram: post.instagram } : {}),
        }
      : {}),

    ...storyData,
    ...(hasFB ? { location: post.location } : {}),
    ...(hasGMB ? parseGoogleBusinessData(post as never) : {}),
    ...(hasLI ? parseLinkedInData(post as never) : {}),
    ...(hasPI ? parsePinterestData(post as never) : {}),
    ...(hasYT ? parseYoutubeData(post as never) : {}),

    // TikTok/Threads: for non-cluster posts, spread data directly.
    // For cluster posts, the cluster block below handles postOptions merging.
    ...(!isCluster && hasTT ? tikTokData : {}),
    ...(!isCluster && hasTH ? threadsData : {}),

    // Cluster-only fields: tags, productTags, location, and platform data
    // that needs special handling (TikTok/Threads postOptions merging)
    ...(isCluster
      ? {
          tags: post.tags,
          productTags: post.productTags,
          location: post.location,
          fileName: post.fileName || null,
          meta: post.meta || postRootMediaData.meta || {},
          thumbnailUrl: post.thumbnailUrl,
          videoCoverImageTimestamp: post?.videoCoverImageTimestamp,
          collaboratingIgUsers: post.collaboratingIgUsers || [],
          // TikTok data without postOptions (will be merged below)
          ...(hasTT
            ? {
                ...(tikTokData as Record<string, unknown>),
                postOptions: undefined,
              }
            : {}),
          // Threads data without postOptions
          ...(hasTH
            ? {
                ...(threadsData as Record<string, unknown>),
                postOptions: undefined,
              }
            : {}),
          // Merge postOptions from TikTok + Threads into a single object
          ...((hasTT || hasTH) &&
          ((tikTokData as Partial<Post>).postOptions ||
            (threadsData as Partial<Post>).postOptions)
            ? {
                postOptions: {
                  ...((tikTokData as Partial<Post>).postOptions || {}),
                  ...((threadsData as Partial<Post>).postOptions || {}),
                },
              }
            : {}),
        }
      : {}),
  } as PostApiInput;

  // Set platforms/platformType on the output — derive from accounts if
  // post.platforms is absent (which it is for form post objects)
  const derivedPlatforms: PlatformTypes[] = post.platforms?.length
    ? platformCodes
    : uniqueAccountPlatforms.map(normalizePlatformTypeForApi);
  if (derivedPlatforms.length > 1) {
    data.platforms = derivedPlatforms;
  } else if (derivedPlatforms.length === 1) {
    data.platformType = derivedPlatforms[0];
  }

  if (
    derivedPlatforms.some((platform: string) =>
      canSelectVideoCoverFrame(platform)
    )
  ) {
    const { videoCoverImageTimestamp, thumbnailUrl, videoCoverType } = post;

    data.videoCoverImageTimestamp = videoCoverImageTimestamp;
    data.videoCoverType = videoCoverType;

    if (thumbnailUrl) {
      data.thumbnailUrl = thumbnailUrl;

      if (Array.isArray(data.mediaFiles) && data.mediaFiles.length > 0) {
        data.mediaFiles = [
          { ...(data.mediaFiles[0] as PostMediaFile), thumbnailUrl },
          ...data.mediaFiles.slice(1),
        ] as PostMediaFile[];
      }
    }
  }

  return data;
}

function parseMediaFileToPostRootData(post: Post): {
  url: string | null;
  originalUrl: string | null;
  origUrl: string | null;
  thumbnailUrl: string | null;
  fileName: string | null;
  isVideo: boolean | null;
  meta: object | null;
  altText: string | null;
} {
  const mediaFiles = post.mediaFiles || [];
  if (mediaFiles.length === 0) {
    if (post.url) {
      return {
        url: post.url ?? null,
        originalUrl: post.originalUrl ?? null,
        origUrl: post.originalUrl ?? null,
        thumbnailUrl: post.thumbnailUrl ?? null,
        fileName: post.fileName ?? null,
        isVideo: post.isVideo ?? null,
        meta: post.meta || {},
        altText: post.altText ?? null,
      };
    }
    /** set null to make sure fields are set in BE */
    return {
      url: null,
      originalUrl: null,
      origUrl: null,
      thumbnailUrl: null,
      fileName: null,
      isVideo: false,
      meta: {},
      altText: null,
    };
  }

  const { url, originalUrl, thumbnailUrl, fileName, isVideo, meta, altText } =
    normalizeMediaFileForApi(mediaFiles[0]);

  return {
    url: url ?? null,
    originalUrl: originalUrl ?? null,
    origUrl: originalUrl ?? null,
    thumbnailUrl: thumbnailUrl ?? null,
    fileName: fileName ?? null,
    isVideo: isVideo ?? null,
    meta: meta ?? null,
    altText: altText ?? null,
  };
}

export function normalizePlatformType(platformType: string): PlatformTypes {
  if (Object.values(PlatformTypes).includes(platformType as PlatformTypes)) {
    return platformType as PlatformTypes;
  }

  switch (platformType) {
    case "IG":
      return PlatformTypes.instagram;

    case "FB":
      return PlatformTypes.facebook;

    case "TW":
      return PlatformTypes.twitter;

    case "LI":
      return PlatformTypes.linkedin;

    case "GMB":
      return PlatformTypes.googlemybusiness;

    case "YT":
      return PlatformTypes.youtube;

    case "PI":
      return PlatformTypes.pinterest;

    case "TT":
      return PlatformTypes.tiktok;

    case "TH":
      return PlatformTypes.threads;

    case "SC":
      return PlatformTypes.snapchat;

    case "OT":
      return PlatformTypes.other;

    default:
      return PlatformTypes.instagram;
  }
}
